import { WidgetWithScheduler } from '../WidgetWithScheduler';
import { Log } from '../../AuxillaryService/Log';
import { $ } from '../../../vendor/jquery';
import {
    ContentLayoutType,
    ContentStoryTemplateData,
    ContentTimelineSlot,
    ContentWidgetConfiguration,
    ContentWidgetConfigurationDetails,
    RawDataListImageStory,
    RawDataListStory,
    RawContentStory,
    ContentStory,
    ReachAPIGetFeedResponse,
    ZodContentWidgetConfigurationDetails,
} from './types';
import { FadeInCommand, FadeOutCommand, WidgetType } from '../types';
import { ReachAPI } from '../../ReachApi';
import { moment } from '../../../vendor/moment';
import { TemplateWidgetMixin, VueTemplate } from '../../Template';
import { DataGetCellResponse, DataGetListResponse, DataListCell, DataListColumn } from './types';
import { glom } from '../../Signage/glom';
import { getZoneById } from '../../Display';
import { feedDisplayName, RSS_FEED_NAMES } from './FeedNames';
import { pushReceiver } from '../../MessagingClient/instance';
import { getNewsStories, NewsStoriesArticle } from '../../ReachApiV2/endpoints/news';
import { isNullish } from '../../Utility/Nullish';

/**
 * NOTE: styles are inlined in templates stored in database for this widget
 * @todo: change templates to use classes rather than inline styles (see related
 * todos below)
 */

export class ContentWidget extends TemplateWidgetMixin(WidgetWithScheduler) {
    readonly type: WidgetType.CONTENT = WidgetType.CONTENT;

    declare readonly config: ContentWidgetConfigurationDetails;

    /**
     * A boolean describing if the scheduler action ran at least once
     */
    private schedulerActionRanOnce: boolean;

    /**
     * A boolean describing in the stories have changed since last render
     */
    private storiesChanged: boolean;

    /**
     * Array of content stories to cycle through
     */
    private readonly stories: ContentStory[];

    /**
     * A unique set of story id_hash used for determining if fetched raw content has new stories
     */
    private readonly uniqueStoryIds: Set<string>;

    /**
     * The stories index for the currently shown story
     */
    private currentStoryIndex: number;

    /**
     * The width of the containing zone
     */
    private readonly width: number;

    /**
     * The height of the containing zone
     */
    private readonly height: number;

    constructor(widgetConfiguration: ContentWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);

        /**
         * Number of seconds between each time the `Scheduler` runs
         */
        this.schedulerRunInterval = 1200; //20 minutes

        /**
         * Used to track whether the schedulerAction function has ran already or not
         */
        this.schedulerActionRanOnce = false;

        this.storiesChanged = false;

        this.stories = [];

        this.uniqueStoryIds = new Set();

        this.currentStoryIndex = 0;

        const zoneInfo = getZoneById(zoneId).returnZoneInfo();

        this.width = zoneInfo?.w || 0;

        this.height = zoneInfo?.h || 0;
    }

    /**
     * Overrides base widget function
     * @TODO: Figure out why the IDE doesn't see this as an override
     */
    protected validateConfigurationDetails(
        maybeContentWidgetConfigurationDetails: ContentWidgetConfigurationDetails
    ): ContentWidgetConfigurationDetails {
        return ZodContentWidgetConfigurationDetails.parse(maybeContentWidgetConfigurationDetails);
    }

    async initialize(): Promise<void> {
        await super.initialize();
        if (this.config.content_type !== 'data-list' && this.config.content_type !== 'custom') {
            pushReceiver().addListener('content-feed-update', (event) => {
                if (event.detail.contentType === this.config.content_type) {
                    this.checkBack();
                }
            });
        }
    }

    /**
     * Calls beforeFadeIn so the content reloads
     */
    async getReadyForDisplay(currentSlot: ContentTimelineSlot): Promise<void> {
        if (currentSlot) {
            await this.beforeFadeIn(currentSlot);
        }
    }

    async stop(): Promise<void> {
        await super.stop();

        // this triggers the content to be re-loaded into the DOM next
        // time beforeFadeIn is called
        this.storiesChanged = true;
    }

    async schedulerAction(): Promise<void> {
        this.schedulerActionRanOnce = true;
        this.checkBack();
    }

    /**
     * triggered once fade out of widget is complete
     * @todo: use css classes rather than inline styles
     */
    async afterFadeOut(currentSlot: ContentTimelineSlot): Promise<void> {
        try {
            $(this.wrapperElement)
                .find(`#content-story-${this.id}-${currentSlot.params.story.id}`)
                .css('left', '-9999999px')
                .css('visibility', 'hidden');
        } catch (e) {
            Log.Error(`Failed to unload content widget: ${this.id}`, null, __fileline);
        }
    }

    /**
     * triggered before fade in occurs
     * @todo: use css classes rather than inline styles
     */
    async beforeFadeIn(currentSlot: ContentTimelineSlot): Promise<Partial<FadeInCommand> | void> {
        try {
            if (this.storiesChanged) {
                await this.renderStories();
                this.storiesChanged = false;
            }
            $(this.wrapperElement)
                .find(`#content-story-${this.id}-${currentSlot.params.story.id}`)
                .css('visibility', 'visible')
                .css('left', '0px');
        } catch (error) {
            Log.Error(`Failed to load content widget: ${this.id}`, null, __fileline, error);
        }
    }

    async beforeFadeOut(): Promise<Partial<FadeOutCommand>> {
        //Do not cancel the fade in/out even if it is the same widget
        return {
            cancelFade: false,
        };
    }

    /**
     * Determines slots to send to timeline
     * @param numSlots - maximum number of slots
     * @param maxDuration - time left in loop
     */
    querySlots(numSlots: number, maxDuration: number): ContentTimelineSlot[] {
        if (!this.schedulerActionRanOnce) {
            // allow the schedule to run at least once even if the widget is paused
            // so that the widget will be able to at least know if it has content to
            // start displaying or not
            this.schedulerAction();
        }

        const slots: ContentTimelineSlot[] = [];

        this.hasContentToPlay = true;
        if (this.stories.length === 0) {
            this.hasContentToPlay = false;
            return slots;
        }

        for (let i = 0; i < numSlots; ++i) {
            // Could implement a method slotParams(..) to provide params to decouple
            // more
            if (this.duration <= maxDuration) {
                const slot = this.generateSlot(this.duration, undefined, {
                    thisloopcount: this.currentStoryIndex,
                    story: this.stories[this.currentStoryIndex],
                });
                slots.push(slot);

                maxDuration -= this.duration;
            } else {
                break;
            }

            this.currentStoryIndex++;
            if (this.currentStoryIndex > this.stories.length - 1) {
                this.currentStoryIndex = 0;
            }
        }

        return slots;
    }

    /**
     * Determines slots to send to timeline
     * @param timeBased - use this.minimumLoopTime to determine how many
     * slots to give the timeline
     */
    querySlotsLinear(timeBased: boolean): ContentTimelineSlot[] {
        const slots: ContentTimelineSlot[] = [];

        if (!this.schedulerActionRanOnce) {
            // allow the schedule to run at least once even if the widget is paused
            // so that the widget will be able to at least know if it has content to
            // start displaying or not
            this.schedulerAction();
        }

        this.hasContentToPlay = true;
        if (this.stories.length === 0) {
            this.hasContentToPlay = false;
            return slots; //defaulting to [] as error
        }

        if (timeBased) {
            let usedTime = 0;

            do {
                const story = this.stories[this.contentStartIndex];

                const slot = this.generateSlot(this.duration, undefined, {
                    thisloopcount: this.currentStoryIndex,
                    story: story,
                });
                slots.push(slot);

                //if the next item is undefined, set contentStartIndex to 0
                this.contentStartIndex =
                    this.stories[this.contentStartIndex + 1] !== undefined
                        ? this.contentStartIndex + 1
                        : 0;
                usedTime += this.duration;
            } while (usedTime < this.minimumLoopTime);
        } else {
            for (let i = 0; i < this.stories.length; i++) {
                const slot = this.generateSlot(this.duration, undefined, {
                    thisloopcount: this.currentStoryIndex,
                    story: this.stories[this.currentStoryIndex],
                });
                slots.push(slot);

                this.currentStoryIndex++;
                if (this.currentStoryIndex > this.stories.length - 1) {
                    this.currentStoryIndex = 0;
                }
            }
        }

        return slots;
    }

    protected async _doPause(): Promise<void> {
        $(this.wrapperElement)
            .find('.story-wrapper')
            .css('left', '-9999999px')
            .css('visibility', 'hidden');
    }

    /**
     * Check back function called every 20 minutes to see if stories changed
     * Remaps data if required
     */
    private async checkBack(): Promise<void> {
        const rawContentStories = await this.loadApiData();
        this.mapStoryObjects(rawContentStories);
    }

    /**
     * Loads ContentStories from the API
     */
    private async loadApiData(): Promise<RawContentStory[]> {
        try {
            if (this.config.content_type === 'data-list') {
                return this.getDataList();
            }
            if (
                this.config.content_type === 'custom' ||
                (RSS_FEED_NAMES as readonly string[]).includes(this.config.content_type)
            ) {
                return this.loadRssData();
            }

            return this.loadNewsStoriesData();
        } catch (error) {
            Log.Warning(
                `Failed to get ${this.config.content_type} data for Content Widget (id:${this.id})`,
                null,
                __fileline,
                error
            );

            throw error;
        }
    }

    /**
     * Get all the data list information from API.
     */
    private async getDataList(): Promise<RawContentStory[]> {
        let rowData: DataGetCellResponse;
        try {
            const security = new ReachAPI.Security(glom.playerUuid, glom.securityToken);
            rowData = await ReachAPI.data.getCells(this.config.data_list, '1:', false, security);
        } catch (error) {
            Log.Warning(
                `Failed to get Cell Data for the Content Widget (id: ${this.id})`,
                null,
                __fileline,
                error
            );

            throw error;
        }

        let columnData: DataGetListResponse;
        try {
            const security = new ReachAPI.Security(glom.playerUuid, glom.securityToken);
            columnData = await ReachAPI.data.getLists(this.config.data_list, security);
        } catch (error) {
            Log.Warning(
                `Failed to get List Data for the Content Widget (id: ${this.id})`,
                null,
                __fileline,
                error
            );

            throw error;
        }

        const columnTypesTaken: string[] = [];
        const columns: DataListColumn[] = [];
        columnData.list.columns.forEach(function (column) {
            //Todo: document what columnTypesTaken is for
            // I can only guess that it is to enforce a required data list shape
            //  Ex: Only the first Image or Text column is used, others ignored
            if (
                (column.type === 'TEXT' || column.type === 'IMAGE') &&
                columnTypesTaken.indexOf(column.type) === -1
            ) {
                columns.push(column);
                columnTypesTaken.push(column.type);
            }
        });

        //note:
        // - first datalist image field - content image;
        // - first text field - content title;
        // - second text field - content description;
        //modifying only the content description does not trigger content
        //layout update
        const dataListStories: Array<RawDataListStory | RawDataListImageStory> = [];
        columns.forEach(function (col) {
            const ordinal = parseInt(col.ordinal);
            const type = col.type;

            rowData.rows.forEach(function (row) {
                const cell: DataListCell = row[ordinal];
                const rowIndex = parseInt(cell.row_ordinal);
                const dataListStory: RawDataListStory = {
                    rowid: cell.row_id,
                    description: cell.value,
                    title: cell.value,
                    long_description: row[ordinal + 1] ? row[ordinal + 1].value : '',
                };

                if (type === 'IMAGE') {
                    dataListStories[rowIndex] = {
                        ...dataListStory,
                        image: cell.value,
                    } as RawDataListImageStory;
                } else {
                    dataListStories[rowIndex] = dataListStory;
                }
            });
        });

        const returnStories: RawContentStory[] = [];
        dataListStories.forEach(function (story) {
            const imageUrl = 'image' in story ? `${ReachAPI.server}/cdn/asset/${story.image}` : '';
            const tempStory: RawContentStory = {
                date: ContentWidget.formatDate(undefined, 'date'), //Creates a current date string
                date_as_timestamp: null,
                description: story.description,
                id_hash: `${story.rowid}${ContentWidget.getHashCode(story.title)}${
                    imageUrl ? ContentWidget.getHashCode(imageUrl) : ''
                }`,
                long_description: story.long_description,
                media: [],
                title: story.title,
            };

            if ('image' in story) {
                tempStory.media.push({ url: imageUrl });
            }

            returnStories.push(tempStory);
        });

        return returnStories;
    }

    private async loadRssData(): Promise<RawContentStory[]> {
        const security = new ReachAPI.Security(glom.playerUuid, glom.securityToken);
        const rssData: ReachAPIGetFeedResponse = await ReachAPI.rss.getFeedForWidget(
            this.id,
            parseInt(this.config.postnum),
            security
        );

        return rssData.feed.items;
    }

    private async loadNewsStoriesData(): Promise<RawContentStory[]> {
        const newsStories = await getNewsStories(this.config.content_type);

        return this.parseNewsStoriesToRawContentStory(newsStories.feed.items);
    }

    private parseNewsStoriesToRawContentStory(
        newsStoryItems: NewsStoriesArticle[]
    ): RawContentStory[] {
        const now = new Date();

        const items: RawContentStory[] = newsStoryItems
            .slice(0, Number(this.config.postnum))
            .map((aNewsStory) => {
                const eventTimeInfo = isNullish(aNewsStory.publishedAt)
                    ? undefined
                    : aNewsStory.publishedAt;
                const filteredMedia = aNewsStory.media.filter(
                    (mediaItem) => !isNullish(mediaItem.url)
                ) as Array<{ url: string }>;
                const mediaForStory = filteredMedia.length > 0 ? filteredMedia : [];
                return {
                    author: aNewsStory.author,
                    date: aNewsStory.date ?? now.toISOString(), //Rss is string and for some reason data list is a moment
                    date_as_timestamp: aNewsStory.date_as_timestamp, //Rss is number, data list is null
                    description: aNewsStory.description ?? '',
                    id_hash: aNewsStory.id_hash,
                    long_description: aNewsStory.long_description ?? '',
                    media: mediaForStory,
                    publishedAt: isNullish(aNewsStory.publishedAt)
                        ? undefined
                        : aNewsStory.publishedAt,
                    title: aNewsStory.title,
                    url: aNewsStory.url,
                    urlToImage: isNullish(aNewsStory.urlToImage)
                        ? undefined
                        : aNewsStory.urlToImage,
                    event_date: eventTimeInfo, //Seemingly only for rss feed data
                    event_time: eventTimeInfo, //Seemingly only for rss feed data
                    event_end_time: undefined, //Seemingly only for rss feed data
                    event_room_name: undefined, //Seemingly only for rss feed data
                };
            });

        return items;
    }

    /**
     * Map story object to template properties. If new story exists, set
     * storiesChanged to true to indicate update of DOM required
     */
    private mapStoryObjects(stories: RawContentStory[]): void {
        const hasNewStory = stories.some((story) => {
            return !this.uniqueStoryIds.has(story.id_hash);
        });

        if (hasNewStory) {
            this.storiesChanged = true;
            this.stories.length = 0;
            this.uniqueStoryIds.clear();

            stories.forEach((story) => {
                const tempStoryObject: ContentStory = {
                    date: story.date,
                    timestamp: story.date_as_timestamp,
                    formattedDate: story.date_as_timestamp
                        ? moment(story.date_as_timestamp * 1000).format('ddd, MMM D, YYYY | h:mma')
                        : '',
                    id: story.id_hash,
                    image: story.media.length > 0 ? story.media[0].url : null,
                    title: story.title?.replace(/(?:https?|ftp):\/\/[\n\S]+/g, '') || null,
                    message: story.description !== '' ? story.description : story.title || null,
                    eventDate: story.event_date || '',
                    eventTime: story.event_time || '',
                    eventEndTime: story.event_end_time || '',
                    eventRoomName: story.event_room_name || '',
                    long_message: story.long_description || null,
                    template: this.config.template,
                };

                //Get rid of links so that it does not appear in signage
                tempStoryObject.message =
                    tempStoryObject.message !== null
                        ? tempStoryObject.message.replace(/(?:https?|ftp):\/\/[\n\S]+/g, '')
                        : null;

                this.stories.push(tempStoryObject);
                this.uniqueStoryIds.add(tempStoryObject.id);
            });
        }
    }

    /**
     * Generate the data object which is used to build templates
     * NOTE -- Two extra properties are added for the template stories data
     */
    protected getRenderContext(): ContentStoryTemplateData {
        return {
            stories: this.stories.map((story) => {
                return {
                    ...story,

                    //Add additional properties used for templates
                    fontResize: story.image === null, //Resize if there is an image url
                    feedType: feedDisplayName(this.config.content_type),
                };
            }),
            wid: this.id.toString(),
            widget: {
                id: this.id,
            },
            metadata: {
                landscape: this.config.landscape.toLowerCase() === 'true',
                fontResize: false,
                fontProperties: {
                    minSize: ContentWidget.getMinimumFontSize(this.width, this.height),
                    maxSize: ContentWidget.getMaximumFontSize(this.width, this.height),
                    ellipsis: true, //Some Vue templates will override internally
                },
            },
            Widget: {
                Id: this.id,
            },
        };
    }

    /**
     * Acquire template and render stories
     * Returns a Promise that resolves when the template has rendered
     */
    private async renderStories(): Promise<void> {
        await this.renderContent();
        this.stories.forEach((story) => this.buildStory(story));
    }

    /**
     * Populate story content and dynamically resize story elements
     * @todo: document what version 1 and version 2 means
     */
    private buildStory(story: ContentStory): void {
        //Check if we are using a Vue templates, which handles itself
        if (this.template instanceof VueTemplate) {
            return;
        }

        //Else do additional Handlebars template work
        const contentWrapperSelector = `${this.id}-${story.id}`;
        if ($(this.wrapperElement).find('.content-story-template-version-2').length) {
            return this.buildStoryVersion2(contentWrapperSelector);
        }

        return this.buildStoryVersion1(contentWrapperSelector, story);
    }

    /**
     * Modify version 1 templates to fit the text and images
     * @todo: Add comments explaining the magic numbers
     * @todo: Refactor to remove the absurd amount of ts-ignore usages.  Required without refactor
     *   that we do not currently have an appetite for since it is working; we just don't want to
     *   know how.
     */
    private buildStoryVersion1(selectorId: string, story: ContentStory): void {
        const imageHeight =
            story.message !== null && story.message !== ''
                ? Math.round(this.height * 0.6)
                : this.height;

        const contentBodyImg = $(this.wrapperElement).find('#content-body-img-' + selectorId);

        if (!contentBodyImg.hasClass('dont-resize')) {
            if (contentBodyImg.hasClass('dont-resize-height')) {
                contentBodyImg.css('height', 'auto');
            } else {
                contentBodyImg.css('height', `${imageHeight}px`);
            }
        }

        const contentBodyMsg = $(this.wrapperElement).find('#content-body-msg-' + selectorId);
        const contentBodyMsg2 = $('#content-body-msg-2-' + selectorId);
        const publishedDate = $(this.wrapperElement).find(
            '#content-story-updated-text-' + selectorId
        );
        const maybePublishedDateHeight = publishedDate.height();

        //if message 2 exists in template
        if (maybePublishedDateHeight) {
            const publishedDateHeight = maybePublishedDateHeight + 20;
            const remainingHeight = this.height - (imageHeight + publishedDateHeight) - 20;

            const isImageTypeFeed = story.image !== null && contentBodyImg.length > 0;
            if (contentBodyMsg2.length > 0) {
                contentBodyMsg2.css(
                    'height',
                    `${isImageTypeFeed ? remainingHeight : this.height - 20}px`
                );
            }

            if (!contentBodyMsg.hasClass('dont-resize')) {
                if (contentBodyMsg.hasClass('dont-resize-height')) {
                    //get height of layer above
                    const maybeContentBodyMsg2Height = contentBodyMsg2.height();

                    if (maybeContentBodyMsg2Height) {
                        contentBodyMsg.css(
                            'height',
                            `${this.height - maybeContentBodyMsg2Height - 40}px`
                        );
                    }
                } else {
                    contentBodyMsg.css(
                        'height',
                        `${isImageTypeFeed ? remainingHeight : this.height - 20}px`
                    );
                }
            }
        }

        const contentBodyLogo = $(this.wrapperElement).find('#content-body-logo-' + selectorId);
        //if logo exists in template
        if (contentBodyLogo.length > 0) {
            contentBodyLogo.html();
        }

        if (story.image === null) {
            contentBodyImg.hide();
        } else {
            contentBodyImg.show();
        }

        if (story.message === null || story.message === '') {
            contentBodyMsg.hide();
        } else {
            contentBodyMsg.show();
        }

        if (story.image !== null) {
            const image: JQuery<HTMLImageElement> = $(
                "<img id='story_img_" + selectorId + "' src='" + story.image + "' />"
            );
            contentBodyImg.append(image);

            //resize image
            const maxImageHeight = imageHeight;

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const maxImageWidth = contentBodyImg.width() - 60;

            image.on('load', function () {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const picWidth: number = image.width();
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const picHeight: number = image.height();

                const widthRatio = ContentWidget.round(maxImageWidth / picWidth, 2);
                const heightRatio = ContentWidget.round(maxImageHeight / picHeight, 2);

                const resizeRatio = widthRatio < heightRatio ? widthRatio : heightRatio;

                const newImageHeight = Math.round(resizeRatio * picHeight);
                const newImageWidth = Math.round(resizeRatio * picWidth);

                if (!contentBodyImg.hasClass('dont-resize')) {
                    image.attr('height', `${newImageHeight}px`);
                    image.attr('width', `${newImageWidth}px`);

                    contentBodyImg.css(
                        'top',
                        `${Math.round((maxImageHeight - newImageHeight) / 2)}px`
                    );
                } else {
                    image.attr('height', '100%');
                    image.attr('width', 'auto');
                }
            });
        }

        const bodyMsgDiv = contentBodyMsg.find('div');
        if (story.message !== null && story.message != '') {
            bodyMsgDiv.html(story.message);
        }

        bodyMsgDiv.css('font-size', '100px'); //reset font size

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        while (bodyMsgDiv.height() >= contentBodyMsg.height()) {
            bodyMsgDiv.css('font-size', `${parseInt(bodyMsgDiv.css('font-size')) - 1}px`);
        }

        if (contentBodyMsg2.length > 0) {
            const bodyMsg2Div = contentBodyMsg2.find('div');
            bodyMsg2Div.html(story.title || '');
            bodyMsg2Div.css('font-size', '100px'); //reset font size

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            while (bodyMsg2Div.height() >= contentBodyMsg2.height()) {
                bodyMsg2Div.css('font-size', `${parseInt(bodyMsg2Div.css('font-size')) - 1}px`);
            }
        }

        const date = story.date || '';
        publishedDate.html(
            date
                ? 'Updated: ' +
                      ContentWidget.formatDate(date, 'date') +
                      ' ' +
                      ContentWidget.formatDate(date, 'time')
                : ''
        );
    }

    /**
     * Modify version 2 templates to fit the text and images
     * @todo: Refactor to remove the absurd amount of ts-ignore usages.  Required without refactor
     *   that we do not currently have an appetite for since it is working; we just don't want to
     *   know how.
     */
    private buildStoryVersion2(selectorId: string): void {
        //initialized to make sure while loop does not go on forever
        const contentStoryContainer = $(this.wrapperElement).find('#content-story-' + selectorId);
        const contentSection = contentStoryContainer.find('.content-section');
        const messageContainer = contentStoryContainer.find('.message-container');
        const messageTitle = messageContainer.find('.message-title');
        const messageContent = messageContainer.find('.message-content');
        const OPTIMUM_FONT_SIZE = ContentWidget.calculateOptimumFontSize(
            contentStoryContainer,
            this.width,
            this.height
        );
        const imageWrapper = contentStoryContainer.find('.image-container');
        const IMAGE_MAX_HEIGHT =
            this.config.landscape === 'true' ? contentSection.height() : imageWrapper.height();

        //if content is overflowing try reducing font-size
        let WHILE_FAIL_SAFE_COUNTER_ENTER = 0;
        let WHILE_FAIL_SAFE_COUNTER_EXIT = 100;
        while (ContentWidget.isContentOverflowing(contentSection)) {
            messageTitle.css('font-size', `${parseInt(messageTitle.css('font-size')) - 1}px`);
            messageContent.css('font-size', `${parseInt(messageContent.css('font-size')) - 1}px`);

            if (parseInt(messageTitle.css('font-size')) < OPTIMUM_FONT_SIZE) {
                break;
            }

            if (WHILE_FAIL_SAFE_COUNTER_ENTER++ > WHILE_FAIL_SAFE_COUNTER_EXIT) {
                break;
            }
        }

        //if content is still overflowing try truncating contents
        if (ContentWidget.isContentOverflowing(contentSection)) {
            let messageContentText = messageContent.text();
            WHILE_FAIL_SAFE_COUNTER_ENTER = 0;
            WHILE_FAIL_SAFE_COUNTER_EXIT = messageContentText.split(' ').length; //max iteration is number of words
            while (ContentWidget.isContentOverflowing(contentSection)) {
                const lastIndex = messageContentText.lastIndexOf(' ');
                messageContentText = messageContentText.substring(0, lastIndex);
                messageContent.text(messageContentText);

                if (WHILE_FAIL_SAFE_COUNTER_ENTER++ > WHILE_FAIL_SAFE_COUNTER_EXIT) {
                    break;
                }
            }
            messageContent.addClass('ellipsis');
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ContentWidget.setImageProperties(imageWrapper, IMAGE_MAX_HEIGHT);
    }

    /**
     * Set image height and width
     * @todo: Refactor to remove the absurd amount of ts-ignore usages.  Required without refactor
     *   that we do not currently have an appetite for since it is working; we just don't want to
     *   know how.
     */
    private static setImageProperties(wrapper: JQuery<HTMLElement>, imageMaxHeight: number): void {
        const image = wrapper.find('img');
        let WHILE_FAIL_SAFE_COUNTER_ENTER;
        let WHILE_FAIL_SAFE_COUNTER_EXIT;
        if (image.length) {
            image.on('error', function () {
                this.parentElement?.remove(); //Remove the image container on error so that text can extend
            });
            image.on('load', function () {
                const maxHeight = imageMaxHeight; //parseInt(0.8 * wrapper.height()),
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const maxWidth: number = 0.9 * wrapper.width();
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const initialWidth: number = image.width();
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                let imageWidth: number = image.width();
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                let imageHeight: number = image.height();
                const ratio = imageWidth / imageHeight; // max-height or max-width is set in CSS these values do not change

                image.css('max-height', imageMaxHeight);
                if (imageHeight > maxHeight || imageWidth > maxWidth) {
                    image.height('auto');
                    image.width('auto');
                } else {
                    WHILE_FAIL_SAFE_COUNTER_ENTER = 0;
                    WHILE_FAIL_SAFE_COUNTER_EXIT = maxWidth / 2; // divide by 2 because width is added 2px at a time
                    while (imageHeight < maxHeight && imageWidth < maxWidth) {
                        imageWidth += 2;
                        imageHeight += 2 * ratio;
                        image.width(`${imageWidth}px`);

                        if (imageWidth === initialWidth) {
                            //if true, it probably means width is set to !important
                            break;
                        }

                        if (WHILE_FAIL_SAFE_COUNTER_ENTER++ > WHILE_FAIL_SAFE_COUNTER_EXIT) {
                            break;
                        }
                    }
                }
            });
        }
    }

    /**
     * Calculate max font size for message
     */
    private static calculateOptimumFontSize(
        postElement: JQuery<HTMLElement>,
        width: number,
        height: number
    ): number {
        //the equation of the trend line y = 26x + 24, where, y is screen width and
        //x is font-size
        const postWidth = postElement.width() || 1;
        if (ContentWidget.getLayoutType(width, height) === ContentLayoutType.PORTRAIT) {
            return Math.round(((postWidth - 24) / 26 + 0.00001) * (height / width));
        }

        //Landscape
        return Math.round((postWidth - 24) / 26 + 0.00001);
    }

    /**
     * Find out if the content is overflowing
     */
    private static isContentOverflowing(
        maybeJQElement: JQuery<HTMLElement> | HTMLElement
    ): boolean {
        const htmlElement: HTMLElement = $(maybeJQElement)[0];
        const curOverflow = htmlElement.style.overflow;

        if (!curOverflow || curOverflow === 'visible') {
            htmlElement.style.overflow = 'hidden';
        }

        htmlElement.style.overflow = curOverflow;

        return (
            htmlElement.clientWidth < htmlElement.scrollWidth ||
            htmlElement.clientHeight < htmlElement.scrollHeight
        );
    }

    /**
     * returns layout type
     */
    private static getLayoutType(width: number, height: number) {
        if (height > width) {
            return ContentLayoutType.PORTRAIT;
        }

        return ContentLayoutType.LANDSCAPE;
    }

    /**
     * Returns a numerical hash code from a string
     */
    private static getHashCode(value: string): number {
        let hash = 0;
        if (value.length == 0) {
            return hash;
        }
        for (let i = 0; i < value.length; i++) {
            const char = value.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash);
    }

    private static round(number: number, places: number): number {
        const multiplier = Math.pow(10, places);
        return Math.round(number * multiplier) / multiplier;
    }

    private static formatDate(dateString?: string, type?: 'date' | 'time') {
        if (type && type.toLowerCase() === 'date') {
            //return date
            return moment(dateString).format('dddd, MMMM Do YYYY');
        } else {
            //return time
            return moment(dateString).format('h:mma');
        }
    }

    /**
     * Uses linear equation to find the minimum possible font size.
     * Returns the minimum font size for the widget text
     * @todo: Document magic numbers
     */
    private static getMinimumFontSize(width: number, height: number): number {
        if (this.getLayoutType(width, height) === ContentLayoutType.PORTRAIT) {
            return Math.round(((width + 209) / 54) * (height / width));
        } else {
            return Math.round((width + 209) / 54);
        }
    }

    /**
     * Uses linear equation to find the maximum possible font size. y = 26x + 24,
     * where, y is screen width and x is font-size, might need improving
     * Returns a number representing the maximum font size for the widget text
     */
    private static getMaximumFontSize(width: number, height: number): number {
        //the equation of the trend line y = 26x + 24, where, y is screen width and
        //x is font-size
        if (this.getLayoutType(width, height) === ContentLayoutType.PORTRAIT) {
            return Math.round(((width - 24) / 26 + 0.00001) * (height / width)) + 4;
        }
        return Math.round((width - 24) / 26 + 0.00001) + 4;
    }
}
